import { useEffect, useState } from 'react';
import { ArrowLeft, Radio, SlidersHorizontal, Clock, TrendingUp, Shuffle, Plus } from 'lucide-react';
import { type Team, vitalsOf, teamTickets } from '@/lib/teams';
import type { Ticket } from '@/lib/mock';
import { DASHBOARDS } from '@/lib/dashboards';
import { useRole } from '@/lib/role';
import { FieldBuilder } from './FieldBuilder';
import { SlaPolicyEditor } from './SlaPolicyEditor';
import { EscalationEditor } from './EscalationEditor';
import { RoutingEditor } from './RoutingEditor';
import { WidgetCard, renderWidget } from './widgets';

export function TeamDashboard({
  team,
  tickets,
  now,
  focusTicketId,
  onBack,
  onNew,
  onOpenTicket,
}: {
  team: Team;
  tickets: Ticket[];
  now: number;
  focusTicketId?: string | null;
  onBack: () => void;
  onNew: () => void;
  onOpenTicket: (ticketId: string) => void;
}) {
  const queue = teamTickets(team, tickets);
  const vitals = vitalsOf(team, tickets, now);
  const config = DASHBOARDS[team.type];
  const ident = `hsl(${team.hue} 65% 52%)`;
  const Icon = team.icon;

  const [builderOpen, setBuilderOpen] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);
  const [escOpen, setEscOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const { role, teamId: myTeamId } = useRole();
  const mayConfigure = (role === 'Super Admin' || role === 'Admin') || (role === 'Team Lead' && myTeamId === team.id);
  useEffect(() => {
    if (focusTicketId) onOpenTicket(focusTicketId);
  }, [focusTicketId, onOpenTicket]);

  return (
    <div className="flex h-full flex-col">

      {/* team header */}
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-3">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border transition hover:bg-muted"
          aria-label="Back to org hub"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{ background: `hsl(${team.hue} 65% 52% / 0.14)`, color: ident }}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-lg font-medium leading-none">{team.name}</div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="capitalize">{team.type === 'it' ? 'IT / Service Desk' : team.type}</span>
            <span>·</span>
            <span>{team.members.length} members</span>
            {team.oncall && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1">
                  <Radio className="h-3 w-3" /> {team.oncall} on-call
                </span>
              </>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">

          {mayConfigure && <>
          <button
            onClick={() => setSlaOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title="SLA policy for this team"
          >
            <Clock className="h-4 w-4" /> SLA policy
          </button>
          <button
            onClick={() => setEscOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title="Escalation chain for this team"
          >
            <TrendingUp className="h-4 w-4" /> Escalation
          </button>
          <button
            onClick={() => setRouteOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title="Auto-routing for this team"
          >
            <Shuffle className="h-4 w-4" /> Routing
          </button>
          <button
            onClick={() => setBuilderOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title="Customize fields for this team"
          >
            <SlidersHorizontal className="h-4 w-4" /> Customize fields
          </button>
          </>}
          <button
            onClick={onNew}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 ml-1"
          >
            <Plus className="h-4 w-4" /> New
          </button>
        </div>
      </div>

      <FieldBuilder open={builderOpen} teamId={team.id} teamName={team.name} onClose={() => setBuilderOpen(false)} />
      <SlaPolicyEditor open={slaOpen} teamId={team.id} teamName={team.name} onClose={() => setSlaOpen(false)} />
      <EscalationEditor open={escOpen} teamId={team.id} teamName={team.name} onClose={() => setEscOpen(false)} />
      <RoutingEditor open={routeOpen} teamId={team.id} teamName={team.name} onClose={() => setRouteOpen(false)} />

      {/* adaptive widget grid — cards hug content (no stretch), wide + dense */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-[1560px] grid-cols-1 items-start gap-3.5 px-6 py-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.widgets.map((w, i) => (
            <WidgetCard key={`${w.type}-${i}`} widget={w}>
              {renderWidget(w, { team, vitals, queue, now, onOpen: onOpenTicket })}
            </WidgetCard>
          ))}
        </div>
      </div>
    </div>
  );
}
